/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import { CategoriesAPI } from "../../../../api/Categories";
import { TagApi } from "../../../../api/Tags";
import { CategoriesAtom } from "../../../../atoms/CategoriesAtom";
import { TagsAtom } from "../../../../atoms/TagsAtom";
import { ICategoryType } from "../../../../generated/Api";
import { ICategory } from "../../../../types/CategoryTypes";
import { ITag } from "../../../../types/TagTypes";
import AutoCompleteCreateCategory from "../../../Settings/Tags/AutoCompleteCreateCategory";
import StyledButton from "../../../StyledButton";
import StyledInput from "../../../StyledInput";
import { useModal } from "../../Modal";
interface Props {
  onCancel: () => void;
  tag?: ITag;
  category?: ICategory;
  isEdit?: boolean;
}

const Title = styled.div`
  color: #2f2f2f;
  font-weight: 600;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 30px 0;
  width: 400px;
  height: 200px;
`;

const Buttons = styled.div`
  margin: 0 -25px;
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #dddddd;
  padding: 20px 20px 0px;
`;

export default function CreateOrUpdateTag(props: Props): JSX.Element {
  const { onCancel, isEdit } = props;
  const [tags, setTags] = useRecoilState<ITag[]>(TagsAtom);
  const [tag, setTag] = useState<Partial<ITag>>(
    props.tag || {
      name: "",
      userUid: "",
      category: "",
    }
  );
  const modal = useModal();
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [categoryInputValue, setCategoryInputValue] = useState(
    props.tag?.category || ""
  );
  const [categories, setCategories] = useRecoilState(CategoriesAtom);
  const [categorySelected, setCategorySelected] = useState<{
    id: string;
    name: string;
    type: string;
  }>(
    props.tag
      ? {
          id: props.tag.categoryId!,
          name: props.tag.category!,
          type: props.tag.type!,
        }
      : {
          id: "",
          name: "",
          type: "",
        }
  );

  const [suggestions, setSuggestions] = useState<
    { id: string; name: string; type: ICategoryType }[]
  >([]);

  useEffect(() => {
    (async () => {
      const categoriesResponse = await CategoriesAPI.readAll();

      if (categoriesResponse) {
        setCategories(categoriesResponse);
      }
    })();
  }, []);

  useEffect(() => {
    const newCategories = categories.map((c) => ({
      id: c._id,
      name: c.name,
      type: c.type,
    }));

    setSuggestions(newCategories);
  }, [categories]);

  async function createTag() {
    if (!tag.name || !categoryInputValue) {
      return toast("Please fill in all the fields", { type: "error" });
    }

    setIsLoading(true);

    const foundSameTag = tags.find(
      (t) => t.name.toLowerCase().trim() === tag.name?.toLowerCase().trim()
    );

    if (!foundSameTag) {
      const tagResponse = await TagApi.create({
        name: tag.name!.replace(/\s\s+/g, " ").trim(),
        category: categoryInputValue.replace(/\s\s+/g, " ").trim(),
      });
      if (tagResponse) {
        setTags((tags) => [
          ...tags,
          {
            ...tagResponse,
            category: categoryInputValue.trim(),
            type: ICategoryType.User,
          },
        ]);

        toast(`Tag succesfully created!`, {
          type: "success",
        });

        modal.popModal();
      }
    } else {
      toast("Can't create tag with same name", { type: "error" });
    }
    setIsLoading(false);
  }

  async function updateTag() {
    if (
      categorySelected.id === "" ||
      categorySelected.name === "" ||
      categorySelected.type == ""
    )
      return;

    setIsLoading(true);
    const tagWithTrim = {
      ...tag,
      name: tag.name?.trim(),
      categoryId: categorySelected.id,
    };

    const tagResponse = await TagApi.update(tagWithTrim as ITag);
    if (tagResponse) {
      modal.popModal();

      toast(`Tag succesfully updated!`, {
        type: "success",
      });

      setTags(
        tags.map((t) =>
          t._id === tagWithTrim._id
            ? ({ ...tagWithTrim, category: categorySelected.name } as ITag)
            : t
        )
      );
    }
    setIsLoading(false);
  }

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  async function handleCategoryValueSelect(option: any) {
    if (option) {
      setCategorySelected(option);
      setCategoryInputValue(option.name);
    }
  }

  useEffect(() => {
    const listener = (event) => {
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        event.preventDefault();
        if (isEdit) {
          updateTag();
        } else {
          createTag();
        }
      }
    };
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, [tag.name, categoryInputValue]);

  return (
    <>
      <InputWrapper>
        <Title>Tag Name</Title>
        <StyledInput
          ref={inputRef}
          value={tag.name}
          onChange={(e) => setTag({ ...tag, name: e.target.value })}
          placeholder="Enter the tag name"
          css={css`
            margin-top: 5px;
          `}
        />

        <Title
          css={css`
            margin: 20px 0px 5px;
          `}
        >
          Tag Category
        </Title>
        <AutoCompleteCreateCategory
          selectedValue={categoryInputValue}
          onValueSelect={(option) => handleCategoryValueSelect(option)}
          onEnter={(option) => handleCategoryValueSelect(option)}
          onValueChange={setCategoryInputValue}
          placeholder="Now enter or select a category for this tag"
          suggestions={suggestions}
          onCancel={() => {
            setCategoryInputValue("");
          }}
          optionIsSelected={(() => {
            if (!categorySelected) return false;
            if (categorySelected.name !== categoryInputValue) return false;
            else return true;
          })()}
        />
      </InputWrapper>

      <Buttons
        css={css`
          display: flex;
          justify-content: flex-end;
        `}
      >
        <StyledButton
          onClick={onCancel}
          inverted
          css={css`
            width: 80px;
            margin-right: 10px;
          `}
        >
          Cancel
        </StyledButton>
        <StyledButton
          disable={(() => {
            if (categoryInputValue === "") return true;
            if (!categorySelected) return true;
            if (categorySelected.name !== categoryInputValue) return true;
            if (tag.name === "") return true;
            else return false;
          })()}
          css={css`
            width: 80px;
          `}
          isLoading={isLoading}
          onClick={() => {
            if (isEdit) updateTag();
            else createTag();
          }}
        >
          Save
        </StyledButton>
      </Buttons>
    </>
  );
}
